'use client'

import Error from '@/components/common/error'
import Loader from '@/components/common/loader'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Checkbox } from '@/components/ui/checkbox'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { isCustomFreeTextQuestionKey } from '@/lib/application/custom-application-questions'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import startCase from 'lodash/startCase'
import upperFirst from 'lodash/upperFirst'
import { CalendarIcon, ChevronDown } from 'lucide-react'
import Image from 'next/image'
import { useEffect, useState } from 'react'

function RequiredLabel({ htmlFor, required, children }) {
  return (
    <Label htmlFor={htmlFor} className="flex items-center gap-1">
      <span>{children}</span>
      {required && <span className="text-error-500">*</span>}
    </Label>
  )
}

function FieldError({ id, error, show }) {
  if (!show || !error) return null
  return (
    <p id={id} className="text-xs text-error-500">
      {error}
    </p>
  )
}

export const applicationItems = {
  car_class: {
    order: 0,
    component: CarClass,
    category: 'game',
  },
  platform: {
    order: 1,
    component: Platform,
    category: 'game',
  },
  driver_number: {
    order: 2,
    component: DriverNumber,
    category: 'game',
  },
  xbox_username: {
    order: 3,
    component: XBOXUsername,
    category: 'game',
  },
  psn_username: {
    order: 4,
    component: PSNUsername,
    category: 'game',
  },
  steam_username: {
    order: 5,
    component: SteamUsername,
    category: 'game',
  },
  iracing_id: {
    order: 6,
    component: IRacingID,
    category: 'game',
  },
  ea_username: {
    order: 7,
    component: EAUsername,
    category: 'game',
  },
  discord_username: {
    order: 8,
    component: DiscordUsername,
    category: 'driver',
  },
  facebook_username: {
    order: 9,
    component: FacebookUsername,
    category: 'driver',
  },
  instagram_username: {
    order: 10,
    component: InstagramUsername,
    category: 'driver',
  },
  twitter_username: {
    order: 11,
    component: TwitterUsername,
    category: 'driver',
  },
  real_name: {
    order: 13,
    component: RealName,
    category: 'driver',
  },
  team_name: {
    order: 14,
    component: TeamName,
    category: 'driver',
  },
  age: {
    order: 15,
    component: Age,
    category: 'driver',
  },
  date_of_birth: {
    order: 16,
    component: DateOfBirth,
    category: 'driver',
  },
  email: {
    order: 17,
    component: Email,
    category: 'driver',
  },
  control_type: {
    order: 18,
    component: ControlType,
    category: 'connect',
  },
  assists: {
    order: 19,
    component: Assists,
    category: 'connect',
  },
  free_text: {
    order: 20,
    component: FreeText,
    category: 'connect',
  },
  rules_link: {
    order: 21,
    component: RulesLink,
    category: 'connect',
  },
  agree_text: {
    order: 22,
    component: AgreementCheckbox,
    category: 'connect',
  },
}

export function getApplicationItem(questionKey) {
  if (isCustomFreeTextQuestionKey(questionKey))
    return applicationItems.free_text
  return applicationItems[questionKey]
}

export function getApplicationQuestionCategory(questionKey) {
  return getApplicationItem(questionKey)?.category
}

export function getBuiltInApplicationQuestionKeys() {
  return Object.keys(applicationItems).filter(
    (key) => !isCustomFreeTextQuestionKey(key),
  )
}

export default function FormComponents(props) {
  if (props.applicationQuestions.length < 1) {
    return <p>No application answers found</p>
  }

  return props.applicationQuestions.map((q) => {
    const item = getApplicationItem(q.key)
    if (!item) return `Unable to load component for "${q.key}"`
    const Component = item.component
    return <Component key={q.key} applicationQuestion={q} {...props} />
  })
}

function ApplicationConfigInput(props) {
  const enabled = props.watch(`${props.propertyName}.enabled`)

  if (props.mandatory) {
    return (
      <span className="flex items-center justify-between gap-2 border border-l-0 border-r-0 border-t-0 border-neutral-blue-700 pb-2 text-sm capitalize text-neutral-blue-200">
        {props.children}

        {!props.forceRequired && (
          <ApplicationItemRequiredInput showBorder={false} {...props} />
        )}
      </span>
    )
  }

  return (
    <div className="h2:font-semibold flex flex-col gap-1 border border-x-0 border-t-0 border-neutral-blue-700 pb-2 text-sm text-neutral-blue-200">
      <div className="flex items-center justify-between gap-2">
        {props.children}

        <div className="flex items-center gap-1">
          {enabled && <ApplicationItemRequiredInput {...props} />}

          <FormField
            control={props.control}
            name={`${props.propertyName}.enabled`}
            render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="my-0!"
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {enabled && props.hiddenSection}
    </div>
  )
}

function ApplicationItemRequiredInput(props) {
  return (
    <FormField
      control={props.control}
      name={`${props.propertyName}.required`}
      render={({ field }) => (
        <FormItem
          className={cn('flex flex-row items-center gap-2 py-1', {
            'me-2 border-r border-neutral-blue-500 pe-3':
              props.showBorder ?? true,
          })}
        >
          <Label>Required?</Label>
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              className="my-0!"
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

function CarClass(props) {
  if (props.carClasses.length === 0) return null

  const resolveCarClassId = (carClass) =>
    carClass?.id ?? carClass?.carClass?.id ?? carClass?.name ?? null

  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.car_class"
        forceRequired
      >
        <h2>Car Class</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const [carClassId, setCarClassId] = useState(
      props.formItems?.car_class ?? null,
    )
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    const { setFormValue } = props

    useEffect(() => {
      setFormValue('car_class', carClassId)
    }, [carClassId, setFormValue])

    // Sync in external changes (e.g. parent auto-fill via async effect)
    useEffect(() => {
      const externalValue = props.formItems?.car_class ?? null
      if (externalValue !== null && externalValue !== carClassId) {
        setCarClassId(externalValue)
      }
    }, [props.formItems?.car_class]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div
        className="grid items-center gap-1.5"
        role="group"
        aria-describedby={error ? 'car-class-error' : undefined}
      >
        <RequiredLabel required={props.applicationQuestion.required}>
          Car Class
        </RequiredLabel>
        <FieldError
          id="car-class-error"
          error={error}
          show={props.showErrors}
        />
        <div className="flex flex-row flex-wrap gap-2">
          {props.carClasses.map((p, index) => {
            const optionId = resolveCarClassId(p)
            const key = optionId ?? `car-class-${index}`
            return (
              <Button
                key={key}
                type="button"
                size="sm"
                variant="outline"
                className={cn('rounded-sm px-4', {
                  'bg-gf-gradient text-shades-0': carClassId === optionId,
                })}
                onClick={() => setCarClassId(optionId)}
              >
                {p.name ?? p.carClass?.name ?? '-'}
              </Button>
            )
          })}
        </div>
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    const carClassAnswer = props.formItems?.car_class
    let displayValue = '-'

    if (typeof carClassAnswer === 'string') {
      displayValue =
        props.carClasses.find((c) => c.id === carClassAnswer)?.name ??
        carClassAnswer
    } else if (carClassAnswer) {
      displayValue =
        carClassAnswer.nicename ||
        carClassAnswer.name ||
        carClassAnswer.carClass?.name ||
        '-'
    }

    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Car Class</h2>

        <p className="text-xs">What car class will the driver use?</p>
        <p className="mt-2 font-semibold text-shades-0">{displayValue}</p>
      </div>
    )
  }
}

function Email(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.email"
      >
        <h2>Email</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="email"
          required={props.applicationQuestion.required}
        >
          Email
        </RequiredLabel>
        <FieldError id="email-error" error={error} show={props.showErrors} />
        <Input
          id="email"
          type="email"
          placeholder="Email"
          value={props.formItems?.email || ''}
          onChange={(e) => props.setFormValue('email', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'email-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Email</h2>

        <p className="text-xs">Email address of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.email}
        </p>
      </div>
    )
}

function DriverNumber(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.driver_number"
      >
        <h2>Driver Number</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="driver_number"
          required={props.applicationQuestion.required}
        >
          Driver Number
        </RequiredLabel>
        <FieldError
          id="driver-number-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="driver_number"
          type="text"
          placeholder="Number"
          value={props.formItems?.driver_number || ''}
          onChange={(e) =>
            props.setFormValue(
              'driver_number',
              e.target.value.replace(/[^\d]/g, ''),
            )
          }
          inputMode="numeric"
          pattern="[0-9]*"
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'driver-number-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Driver Number</h2>

        <p className="text-xs">
          The driver will only be able to choose a unique number.
        </p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.driver_number}
        </p>
      </div>
    )
}

function XBOXUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.xbox_username"
      >
        <h2>XBOX Username</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="xbox_username"
          required={props.applicationQuestion.required}
        >
          XBOX Username
        </RequiredLabel>
        <FieldError
          id="xbox-username-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="xbox_username"
          type="text"
          placeholder="XBOX username"
          value={props.formItems?.xbox_username || ''}
          onChange={(e) => props.setFormValue('xbox_username', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'xbox-username-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">XBOX Username</h2>

        <p className="text-xs">XBOX username of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.xbox_username}
        </p>
      </div>
    )
}

function PSNUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.psn_username"
      >
        <h2>PSN Username</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="psn_username"
          required={props.applicationQuestion.required}
        >
          PSN Username
        </RequiredLabel>
        <FieldError
          id="psn-username-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="psn_username"
          type="text"
          placeholder="PSN username"
          value={props.formItems?.psn_username || ''}
          onChange={(e) => props.setFormValue('psn_username', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'psn-username-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">PSN Username</h2>

        <p className="text-xs">PSN username of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.psn_username}
        </p>
      </div>
    )
}

function SteamUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.steam_username"
      >
        <h2>Steam ID</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    // Determine if the value was auto-filled
    const isAutoFilled =
      props.formItems?.steam_username &&
      props.formItems.steam_username !== '' &&
      props.formItems._autoFilled?.steam_username === true
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="steam_id"
          required={props.applicationQuestion.required}
        >
          Steam ID
        </RequiredLabel>
        <FieldError id="steam-id-error" error={error} show={props.showErrors} />
        {props.accountsLoading.some((k) => k === 'steam') ? (
          <div className="flex items-center gap-2">
            <Loader size="sm" />{' '}
            <span className="text-sm">Fetching your Steam ID...</span>
          </div>
        ) : (
          <>
            <Input
              id="steam_id"
              type="text"
              placeholder="Steam ID"
              value={props.formItems?.steam_username || ''}
              onChange={(e) =>
                props.setFormValue('steam_username', e.target.value)
              }
              required={props.applicationQuestion.required}
              disabled={isAutoFilled}
              aria-invalid={!!error}
              aria-describedby={error ? 'steam-id-error' : undefined}
            />
            {isAutoFilled ? (
              <p className="text-xs text-success-500">
                <span className="font-semibold">✓ Automatically filled</span>{' '}
                from your connected Steam account
              </p>
            ) : (
              <>
                <p className="p-2 text-xs">
                  Don&apos;t know your Steam ID? Use{' '}
                  <a
                    href="https://www.steamidfinder.com/"
                    target="_blank"
                    className="underline"
                  >
                    Steam ID Finder
                  </a>{' '}
                  to get it! Example:
                </p>
                <div className="pl-2">
                  <Image
                    src="/img/steam-id-finder-example.webp"
                    className="rounded-sm"
                    width="291"
                    height="33"
                    alt="Steam ID Finder Example"
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Steam ID</h2>

        <p className="text-xs">Steam ID of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.steam_username}
        </p>
      </div>
    )
  }
}

function IRacingID(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.iracing_id"
      >
        <h2>IRacing ID</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    // Determine if the value was auto-filled
    const isAutoFilled =
      props.formItems?.iracing_id &&
      props.formItems.iracing_id !== '' &&
      props.formItems._autoFilled?.iracing_id === true
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="iracing_id"
          required={props.applicationQuestion.required}
        >
          IRacing ID
        </RequiredLabel>
        <FieldError
          id="iracing-id-error"
          error={error}
          show={props.showErrors}
        />
        {props.accountsLoading.some((k) => k === 'iracing') ? (
          <div className="flex items-center gap-2">
            <Loader size="sm" />{' '}
            <span className="text-sm">Fetching your iRacing ID...</span>
          </div>
        ) : (
          <>
            <Input
              id="iracing_id"
              type="text"
              placeholder="IRacing ID"
              value={props.formItems?.iracing_id || ''}
              onChange={(e) =>
                props.setFormValue(
                  'iracing_id',
                  e.target.value.replace(/[^\d]/g, ''),
                )
              }
              inputMode="numeric"
              pattern="[0-9]*"
              required={props.applicationQuestion.required}
              disabled={isAutoFilled}
              aria-invalid={!!error}
              aria-describedby={error ? 'iracing-id-error' : undefined}
            />
            {isAutoFilled && (
              <p className="text-xs text-success-500">
                <span className="font-semibold">✓ Automatically filled</span>{' '}
                from your connected iRacing account
              </p>
            )}
          </>
        )}
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">IRacing ID</h2>

        <p className="text-xs">IRacing ID of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.iracing_id}
        </p>
      </div>
    )
  }
}

function EAUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.ea_username"
      >
        <h2>EA Username</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="ea_username"
          required={props.applicationQuestion.required}
        >
          EA Username
        </RequiredLabel>
        <FieldError
          id="ea-username-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="ea_username"
          type="text"
          placeholder="EA Username"
          value={
            props.formItems?.ea_username ||
            props.formItems?.codemasters_username ||
            ''
          }
          onChange={(e) => props.setFormValue('ea_username', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'ea-username-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">EA Username</h2>

        <p className="text-xs">EA Username of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.ea_username ||
            props.formItems?.codemasters_username}
        </p>
      </div>
    )
}

function DiscordUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.discord_username"
      >
        <h2>Discord Username</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    // Determine if the value was auto-filled
    const isAutoFilled =
      props.formItems?.discord_username &&
      props.formItems.discord_username !== '' &&
      props.formItems._autoFilled?.discord_username === true
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="discord_username"
          required={props.applicationQuestion.required}
        >
          Discord Username
        </RequiredLabel>
        <FieldError
          id="discord-username-error"
          error={error}
          show={props.showErrors}
        />
        {props.accountsLoading.some((k) => k === 'discord') ? (
          <div className="flex items-center gap-2">
            <Loader size="sm" />{' '}
            <span className="text-sm">Fetching your Discord username...</span>
          </div>
        ) : (
          <>
            <Input
              id="discord_username"
              type="text"
              placeholder="Discord Username"
              value={props.formItems?.discord_username || ''}
              onChange={(e) =>
                props.setFormValue('discord_username', e.target.value)
              }
              required={props.applicationQuestion.required}
              disabled={isAutoFilled}
              aria-invalid={!!error}
              aria-describedby={error ? 'discord-username-error' : undefined}
            />
            {isAutoFilled && (
              <p className="text-xs text-success-500">
                <span className="font-semibold">✓ Automatically filled</span>{' '}
                from your connected Discord account
              </p>
            )}
          </>
        )}
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Discord Username</h2>

        <p className="text-xs">Discord Username of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.discord_username}
        </p>
      </div>
    )
  }
}

function FacebookUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.facebook_username"
      >
        <h2>Facebook Username</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="facebook_username"
          required={props.applicationQuestion.required}
        >
          Facebook Username
        </RequiredLabel>
        <FieldError
          id="facebook-username-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="facebook_username"
          type="text"
          placeholder="Facebook Username"
          value={props.formItems?.facebook_username || ''}
          onChange={(e) =>
            props.setFormValue('facebook_username', e.target.value)
          }
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'facebook-username-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Facebook Username</h2>

        <p className="text-xs">Facebook Username of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.facebook_username}
        </p>
      </div>
    )
}

function InstagramUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.instagram_username"
      >
        <h2>Instagram Username</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="instagram_username"
          required={props.applicationQuestion.required}
        >
          Instagram Username
        </RequiredLabel>
        <FieldError
          id="instagram-username-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="instagram_username"
          type="text"
          placeholder="Instagram Username"
          value={props.formItems?.instagram_username || ''}
          onChange={(e) =>
            props.setFormValue('instagram_username', e.target.value)
          }
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'instagram-username-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Instagram Username</h2>

        <p className="text-xs">Instagram Username of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.instagram_username}
        </p>
      </div>
    )
}

function TwitterUsername(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.twitter_username"
      >
        <h2>Twitter Handle</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="twitter_username"
          required={props.applicationQuestion.required}
        >
          Twitter Handle
        </RequiredLabel>
        <FieldError
          id="twitter-username-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="twitter_username"
          type="text"
          placeholder="Twitter Handle"
          value={props.formItems?.twitter_username || ''}
          onChange={(e) =>
            props.setFormValue('twitter_username', e.target.value)
          }
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'twitter-username-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Twitter Handle</h2>

        <p className="text-xs">Twitter Handle of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.twitter_username}
        </p>
      </div>
    )
}

function RealName(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.real_name"
      >
        <h2>Driver's Name</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="real_name"
          required={props.applicationQuestion.required}
        >
          Your Real Name
        </RequiredLabel>
        <FieldError
          id="real-name-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="real_name"
          type="text"
          placeholder="Name"
          autoComplete="name"
          value={props.formItems?.real_name || ''}
          onChange={(e) => props.setFormValue('real_name', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'real-name-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Driver's Name</h2>

        <p className="text-xs">Real name of the driver.</p>
        <p className="mt-2 font-semibold capitalize text-shades-0">
          {props.formItems?.real_name}
        </p>
      </div>
    )
}

function TeamName(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.team_name"
      >
        <h2>Team name</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="team_name"
          required={props.applicationQuestion.required}
        >
          Team name
        </RequiredLabel>
        <FieldError
          id="team-name-error"
          error={error}
          show={props.showErrors}
        />
        <Input
          id="team_name"
          type="text"
          placeholder="Name"
          value={props.formItems?.team_name || ''}
          onChange={(e) => props.setFormValue('team_name', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'team-name-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Team name</h2>

        <p className="text-xs">Driver's team.</p>
        <p className="mt-2 font-semibold capitalize text-shades-0">
          {props.formItems?.team_name}
        </p>
      </div>
    )
}

function Age(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.age"
      >
        <h2>Age</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="age"
          required={props.applicationQuestion.required}
        >
          Age
        </RequiredLabel>
        <FieldError id="age-error" error={error} show={props.showErrors} />
        <Input
          id="age"
          type="number"
          min="1"
          max="120"
          placeholder="Your age"
          value={props.formItems?.age || ''}
          onChange={(e) => props.setFormValue('age', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'age-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Age</h2>
        <p className="text-xs">Age of the driver in years.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.age ? `${props.formItems.age} years old` : '-'}
        </p>
      </div>
    )
  }
}

function DateOfBirth(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.date_of_birth"
      >
        <h2>Date of Birth</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    // Initialize with date_of_birth if available
    const [dateOfBirth, setDateOfBirth] = useState(() => {
      if (props.formItems?.date_of_birth) {
        return new Date(props.formItems.date_of_birth)
      }
      return null
    })
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="date_of_birth"
          required={props.applicationQuestion.required}
        >
          Date of Birth
        </RequiredLabel>
        <FieldError
          id="date-of-birth-error"
          error={error}
          show={props.showErrors}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="date_of_birth"
              variant="outline"
              className={cn(
                'w-full pl-5 text-left font-normal',
                !dateOfBirth && 'text-muted-foreground',
              )}
              aria-invalid={!!error}
              aria-describedby={error ? 'date-of-birth-error' : undefined}
            >
              <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
              {dateOfBirth ? (
                dayjs(dateOfBirth).format('MMMM D, YYYY')
              ) : (
                <span>Pick a date</span>
              )}
              <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              selected={dateOfBirth}
              onSelect={(date) => {
                setDateOfBirth(date)
                props.setFormValue('date_of_birth', date)
              }}
              disabled={{ after: new Date() }}
            />
          </PopoverContent>
        </Popover>
        {dateOfBirth && (
          <p className="text-xs text-neutral-blue-300">
            Age: {dayjs().diff(dayjs(dateOfBirth), 'year')}
          </p>
        )}
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    let formattedDob = null
    let calculatedAge = null
    let dob = null

    // Format the date of birth if it exists
    if (props.formItems?.date_of_birth) {
      try {
        const dateValue = props.formItems.date_of_birth
        dob =
          typeof dateValue === 'string'
            ? new Date(dateValue)
            : dateValue instanceof Date
              ? dateValue
              : null

        // Only format if we have a valid date
        if (dob && !isNaN(dob.getTime())) {
          formattedDob = dayjs(dob).format('MMMM D, YYYY')
          calculatedAge = dayjs().diff(dayjs(dob), 'year')
        }
      } catch (e) {
        console.error('Error parsing date of birth:', e)
      }
    }

    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Date of Birth</h2>
        <p className="text-xs">Date of birth of the driver.</p>
        <p className="mt-2 font-semibold text-shades-0">
          {formattedDob ? `${formattedDob} (${calculatedAge} years old)` : '-'}
        </p>
      </div>
    )
  }
}

function ControlType(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.control_type"
      >
        <h2>Control Type</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const [controlType, setControlType] = useState(props.formItems.control_type)
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    const { setFormValue } = props

    useEffect(() => {
      setFormValue('control_type', controlType)
    }, [controlType, setFormValue])

    return (
      <div
        className="grid items-center gap-1.5"
        role="group"
        aria-describedby={error ? 'control-type-error' : undefined}
      >
        <RequiredLabel required={props.applicationQuestion.required}>
          Control Type
        </RequiredLabel>
        <FieldError
          id="control-type-error"
          error={error}
          show={props.showErrors}
        />
        <div className="flex flex-row flex-wrap gap-2">
          {['wheel', 'controller', 'keyboard', 'other'].map((t) => (
            <Button
              key={t}
              type="button"
              size="sm"
              variant="outline"
              className={cn('rounded-sm', {
                'bg-gf-gradient text-shades-0': controlType === t,
              })}
              onClick={() => setControlType(t)}
            >
              {startCase(t)}
            </Button>
          ))}
        </div>
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Control Type</h2>

        <p className="text-xs">Does the driver use a wheel or a controller?</p>
        <p className="mt-2 font-semibold capitalize text-shades-0">
          {props.formItems?.control_type}
        </p>
      </div>
    )
}

function Platform(props) {
  const platforms = (props.platforms || []).map((p) => p.platform || p)
  const platformsLoading = false
  const platformsError = null

  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.platform"
      >
        <h2>Platform</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const [controlType, setControlType] = useState(props.formItems.platform)
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    const { setFormValue, viewing } = props

    useEffect(() => {
      if (!viewing) setFormValue('platform', controlType)
    }, [controlType, setFormValue, viewing])

    return (
      <div
        className="grid items-center gap-1.5"
        role="group"
        aria-describedby={error ? 'platform-error' : undefined}
      >
        <RequiredLabel required={props.applicationQuestion.required}>
          Platform
        </RequiredLabel>
        <FieldError id="platform-error" error={error} show={props.showErrors} />
        <div className="flex flex-row flex-wrap gap-2">
          {platformsError ? (
            <Error
              icon="exclamation"
              size="alert"
              className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
              error={platformsError}
            />
          ) : platformsLoading ? (
            <Loader size="md" className="mx-auto mt-[60px] w-full max-w-7xl" />
          ) : (
            platforms.map((p) => {
              const platformValue =
                p.name || p.platformName || (typeof p === 'string' ? p : '')
              const platformLabel =
                p.nicename || p.platformName || (typeof p === 'string' ? p : '')

              return (
                <Button
                  key={platformValue}
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn('rounded-sm', {
                    'bg-gf-gradient text-shades-0':
                      props.formItems.platform === platformValue,
                  })}
                  onClick={() => setControlType(platformValue)}
                >
                  {platformLabel}
                </Button>
              )
            })
          )}
        </div>
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    const platformAnswer = props.formItems?.platform

    let displayValue = '-'
    if (typeof platformAnswer === 'string') {
      displayValue = platformAnswer
    } else if (platformAnswer) {
      displayValue =
        platformAnswer.nicename ||
        platformAnswer.platformName ||
        platformAnswer.name ||
        '-'
    }

    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Platform</h2>

        <p className="text-xs">What platform does the user play on?</p>
        <p className="mt-2 font-semibold uppercase text-shades-0">
          {displayValue}
        </p>
      </div>
    )
  }
}

function Assists(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.assists"
        hiddenSection={
          <FormField
            control={props.control}
            name="questions.assists.label"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Notes or clarification for the driver's response"
                    {...field}
                    value={field.value || ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        }
      >
        <h2>Assists</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor="assists"
          required={props.applicationQuestion.required}
        >
          Assists
        </RequiredLabel>
        <FieldError id="assists-error" error={error} show={props.showErrors} />
        {props.applicationQuestion.label && (
          <p>{props.applicationQuestion.label}</p>
        )}
        <Input
          id="assists"
          type="text"
          placeholder="I use..."
          onChange={(e) => props.setFormValue('assists', e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? 'assists-error' : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing)
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Assists</h2>

        <p className="text-xs">What assists does the driver use?</p>
        <p className="mt-2 font-semibold capitalize text-shades-0">
          {props.formItems?.assists}
        </p>
      </div>
    )
}

function RulesLink(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.rules_link"
        hiddenSection={
          <FormField
            control={props.control}
            name="questions.rules_link.label"
            render={({ field }) => (
              <FormItem>
                <FormLabel>URL</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Link to rules (include https://)"
                    {...field}
                    value={field.value || ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        }
      >
        <h2>Link to rules</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div
        className="grid items-center gap-1.5"
        role="group"
        aria-describedby={error ? 'rules-link-error' : undefined}
      >
        <RequiredLabel required={props.applicationQuestion.required}>
          Link to rules
        </RequiredLabel>
        <FieldError
          id="rules-link-error"
          error={error}
          show={props.showErrors}
        />
        <a
          onClick={() => props.setFormValue('rules_link', 'clicked')}
          target={'_blank'}
          href={props.applicationQuestion.label}
          className="gf-gradient-text font-semibold"
        >
          Read Rules
        </a>
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">Rules</h2>

        <p className="text-xs">
          Make sure drivers have read your league's rules.
        </p>

        {props.viewing && (
          <p className="mt-2 font-semibold text-shades-0">
            Link to the rules was{' '}
            <span
              className={
                props.formItems?.rules_link === 'clicked'
                  ? 'text-success-500'
                  : 'text-error-500'
              }
            >
              {props.formItems?.rules_link === 'clicked'
                ? 'clicked'
                : 'not clicked'}
            </span>
          </p>
        )}
      </div>
    )
  }
}

function FreeText(props) {
  const questionKey = props.applicationQuestion.key
  const inputId = `free-text-${questionKey}`
  const errorId = `${inputId}-error`
  const label = props.applicationQuestion.label || 'Custom question'

  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName={`questions.${questionKey}`}
        hiddenSection={
          <FormField
            control={props.control}
            name={`questions.${questionKey}.label`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Question Text</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="E.g Do you have a real race license?"
                    {...field}
                    value={field.value || ''}
                    onChange={(event) => {
                      field.onChange(event)
                      void props.trigger?.(`questions.${questionKey}.label`)
                      void props.trigger?.('questions')
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        }
      >
        <h2>Custom Question</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="grid items-center gap-1.5">
        <RequiredLabel
          htmlFor={inputId}
          required={props.applicationQuestion.required}
        >
          {upperFirst(label)}
        </RequiredLabel>
        <FieldError id={errorId} error={error} show={props.showErrors} />
        <Input
          id={inputId}
          type="text"
          placeholder="Your answer"
          value={props.formItems?.[questionKey] || ''}
          onChange={(e) => props.setFormValue(questionKey, e.target.value)}
          required={props.applicationQuestion.required}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">
          {label || 'Free text field question'}
        </h2>

        <p className="text-xs">Custom question for the driver</p>
        <p className="mt-2 font-semibold text-shades-0">
          {props.formItems?.[questionKey] || '-'}
        </p>
      </div>
    )
  }
}

function AgreementCheckbox(props) {
  if (props.editing) {
    return (
      <ApplicationConfigInput
        {...props}
        control={props.control}
        propertyName="questions.agree_text"
        hiddenSection={
          <FormField
            control={props.control}
            name="questions.agree_text.label"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custom Text</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="E.g Do you agree to receive marketing notifications?"
                    {...field}
                    value={field.value || ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        }
      >
        <h2>Optional Agreement Checkbox with Custom Text</h2>
      </ApplicationConfigInput>
    )
  } else if (props.applying) {
    const error = props.showErrors
      ? props.validationErrors?.[props.applicationQuestion.key]
      : null

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-1">
          <Checkbox
            id="agree_text"
            checked={!!props.formItems?.agree_text}
            onCheckedChange={(checked) =>
              props.setFormValue('agree_text', checked === true)
            }
            required={props.applicationQuestion.required}
            aria-invalid={!!error}
            aria-describedby={error ? 'agree-text-error' : undefined}
          />

          <RequiredLabel
            htmlFor="agree_text"
            required={props.applicationQuestion.required}
          >
            {props.applicationQuestion.label ||
              'Do you agree to our championship rules?'}
          </RequiredLabel>

          {!props.applicationQuestion.required && (
            <span className="text-neutral-blue-300">(Optional)</span>
          )}
        </div>
        <FieldError
          id="agree-text-error"
          error={error}
          show={props.showErrors}
        />
      </div>
    )
  } else if (props.viewing || props.reviewing) {
    const agreeText = props.applicationQuestions?.find(
      (q) => q.key === 'agree_text',
    )

    return (
      <div className="text-neutral-blue-300">
        <h2 className="text-sm font-semibold">
          {agreeText?.label || 'Do you agree to our championship rules?'}
        </h2>

        <p className="text-xs">What would you like your drivers to agree to?</p>

        {props.viewing && (
          <p className="mt-2 font-semibold text-shades-0">
            {props.formItems?.agree_text ? 'Checked / Yes' : 'Not Checked / No'}
          </p>
        )}
      </div>
    )
  }
}
